/* eslint-disable max-lines */
/**
 * Centralized artwork file type validation and configuration
 * Used across Design Lab, design-upload-cta, and other upload components
 *
 * CRITICAL: This validation must stay in sync with Constants::PRINT_SUPPORTED_MIME_TYPES
 * in the backend to prevent uploads that pass frontend validation but fail backend validation.
 */

/* eslint-disable max-lines-per-function */
/**
 * Materializes a file by reading its content into memory.
 * This is necessary for files from cloud storage providers (Google Drive, Dropbox)
 * on mobile devices where the File object may be a virtual reference.
 * Reading the file forces the browser/OS to download the actual content.
 *
 * @param {File} file - The file to materialize
 * @param {Object} options - Optional configuration
 * @param {number} options.timeoutMs - Timeout in milliseconds (default: 60000 - 60 seconds)
 * @param {Function} options.onProgress - Optional callback for progress updates
 * @returns {Promise<{ file: File, error: string | null }>} - The materialized file or error
 */
export const materializeFile = (file, options = {}) =>
  new Promise(resolve => {
    const { timeoutMs = 60000, onProgress } = options;

    // Check for obviously empty files
    if (file.size === 0) {
      resolve({
        file: null,
        error: "The selected file appears to be empty. Please select a different file."
      });
      return;
    }

    const reader = new FileReader();
    let timeoutId = null;
    let resolved = false;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const safeResolve = result => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(result);
    };

    // Set up timeout for slow cloud storage reads
    timeoutId = setTimeout(() => {
      if (!resolved) {
        reader.abort();
        safeResolve({
          file: null,
          error:
            "File download timed out. If selecting from cloud storage (Google Drive, Dropbox), please download the file to your device first and try again."
        });
      }
    }, timeoutMs);

    reader.onprogress = event => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };

    reader.onload = () => {
      try {
        const arrayBuffer = reader.result;

        // Verify we got actual content
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          safeResolve({
            file: null,
            error:
              "Unable to read file content. The file may still be downloading or is not accessible."
          });
          return;
        }

        // Create a new File from the ArrayBuffer to ensure content is in memory
        const materializedFile = new File([arrayBuffer], file.name, {
          type: file.type,
          lastModified: file.lastModified
        });

        safeResolve({ file: materializedFile, error: null });
      } catch (err) {
        safeResolve({
          file: null,
          error: "Failed to process file. Please try again or select a different file."
        });
      }
    };

    reader.onerror = () => {
      safeResolve({
        file: null,
        error:
          "Unable to read the file. If you're selecting from cloud storage (Google Drive, Dropbox), please ensure the file is fully downloaded or try downloading it to your device first."
      });
    };

    reader.onabort = () => {
      // Only resolve if not already resolved (timeout case handles its own resolution)
      if (!resolved) {
        safeResolve({
          file: null,
          error: "File reading was cancelled."
        });
      }
    };

    // Reading as ArrayBuffer forces the browser to actually fetch the file content
    // from cloud storage providers
    reader.readAsArrayBuffer(file);
  });
/* eslint-enable max-lines-per-function */

// Design Lab hard-block threshold: largest side must be at least 200px (JED-431).
export const MIN_IMAGE_DIMENSION_PX = 200;

// Must match DtfArtwork::MAX_IMAGE_BYTE_SIZE on the backend
const MAX_FILE_SIZE_BYTES = 40 * 1024 * 1024;

const validateFileSize = file => {
  const valid = file.size <= MAX_FILE_SIZE_BYTES;
  const message = valid
    ? ""
    : `File is too large. Maximum allowed size is ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB.`;
  return { valid, message };
};

// Accept configuration for HTML file inputs (react-dropzone format) - single source of truth
export const ACCEPT_CONFIG = {
  "image/bmp": [".bmp"],
  "image/gif": [".gif"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/svg+xml": [".svg"],
  "image/tiff": [".tiff", ".tif"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"]
};

// Derived arrays from ACCEPT_CONFIG - no duplication
export const SUPPORTED_IMAGE_MIME_TYPES = Object.keys(ACCEPT_CONFIG).filter(mimeType =>
  mimeType.startsWith("image/")
);

export const SUPPORTED_DOCUMENT_MIME_TYPES = Object.keys(ACCEPT_CONFIG).filter(mimeType =>
  mimeType.startsWith("application/")
);

/**
 * Gets MIME type from file extension as fallback
 * Used when file.type is empty (common on Android with cloud providers like Dropbox/Google Drive)
 * @param {string} fileName - The file name to extract extension from
 * @param {Object} config - The accept config to search (defaults to ACCEPT_CONFIG)
 * @returns {string|null} - The MIME type or null if not found
 */
const getMimeTypeFromExtension = (fileName, config = ACCEPT_CONFIG) => {
  if (!fileName) return null;

  const extension = fileName.toLowerCase().match(/\.[^.]+$/)?.[0];
  if (!extension) return null;

  for (const [mimeType, extensions] of Object.entries(config)) {
    if (extensions.includes(extension)) {
      return mimeType;
    }
  }

  return null;
};

/**
 * Validates if a file type is supported for artwork upload
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, message: string }} - { valid: boolean, message: string }
 */
export const validateArtworkFileType = file => {
  // Check supported MIME types (strict whitelist matching backend)
  let isValidMimeType =
    SUPPORTED_IMAGE_MIME_TYPES.includes(file.type) ||
    SUPPORTED_DOCUMENT_MIME_TYPES.includes(file.type);

  // Fallback: check file extension when MIME type is empty
  // This handles Android files from cloud providers (Dropbox, Google Drive) that don't provide MIME type
  if (!isValidMimeType && !file.type) {
    const mimeTypeFromExtension = getMimeTypeFromExtension(file.name);
    isValidMimeType =
      SUPPORTED_IMAGE_MIME_TYPES.includes(mimeTypeFromExtension) ||
      SUPPORTED_DOCUMENT_MIME_TYPES.includes(mimeTypeFromExtension);
  }

  const message = isValidMimeType ? "" : "You can't upload files of this type";
  return { valid: isValidMimeType, message };
};

/**
 * Get accept string for HTML file input
 * @returns {string} - Comma-separated accept string
 */
export const getAcceptString = () => {
  const acceptArray = [];

  for (const [mimeType, extensions] of Object.entries(ACCEPT_CONFIG)) {
    acceptArray.push(mimeType);
    acceptArray.push(...extensions);
  }

  return acceptArray.join(",");
};

export const DESIGN_LAB_ACCEPT_CONFIG = {
  "image/bmp": [".bmp"],
  "image/gif": [".gif"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/svg+xml": [".svg"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
  "image/tiff": [".tiff", ".tif"]
};

export const DESIGN_LAB_PREVIEW_BASED_EXTENSIONS = /^image\/(png|jpeg|jpg)$/;

// Derived arrays from DESIGN_LAB_ACCEPT_CONFIG
export const DESIGN_LAB_SUPPORTED_IMAGE_MIME_TYPES = Object.keys(DESIGN_LAB_ACCEPT_CONFIG).filter(
  mimeType => mimeType.startsWith("image/")
);

export const DESIGN_LAB_SUPPORTED_DOCUMENT_MIME_TYPES = Object.keys(
  DESIGN_LAB_ACCEPT_CONFIG
).filter(mimeType => mimeType.startsWith("application/"));

/**
 * Validates if a file type is supported for design lab artwork upload
 * Excludes PDF, HEIC, and TIFF formats
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, message: string }} - { valid: boolean, message: string }
 */
export const validateDesignLabArtworkFileType = file => {
  // Check supported MIME types for design lab (excludes PDF, HEIC, TIFF)
  let isValidMimeType =
    DESIGN_LAB_SUPPORTED_IMAGE_MIME_TYPES.includes(file.type) ||
    DESIGN_LAB_SUPPORTED_DOCUMENT_MIME_TYPES.includes(file.type);

  // Fallback: check file extension when MIME type is empty
  // This handles Android files from cloud providers (Dropbox, Google Drive) that don't provide MIME type
  if (!isValidMimeType && !file.type) {
    const mimeTypeFromExtension = getMimeTypeFromExtension(file.name, DESIGN_LAB_ACCEPT_CONFIG);
    isValidMimeType =
      DESIGN_LAB_SUPPORTED_IMAGE_MIME_TYPES.includes(mimeTypeFromExtension) ||
      DESIGN_LAB_SUPPORTED_DOCUMENT_MIME_TYPES.includes(mimeTypeFromExtension);
  }

  const message = isValidMimeType ? "" : "You can't upload files of this type";
  return { valid: isValidMimeType, message };
};

/**
 * Validates image dimensions for raster images (PNG/JPEG)
 * Ensures the largest dimension is at least 200px
 * @param {File} file - The file to validate
 * @returns {Promise<{ valid: boolean, message: string }>} - Promise resolving to { valid: boolean, message: string }
 */
export const validateImageDimensions = file =>
  new Promise(resolve => {
    // Only validate PNG and JPEG files
    if (!file.type.match(DESIGN_LAB_PREVIEW_BASED_EXTENSIONS)) {
      resolve({ valid: true, message: "" });
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const largestDimension = Math.max(img.width, img.height);
      const isValid = largestDimension >= MIN_IMAGE_DIMENSION_PX;

      const message = isValid
        ? ""
        : `Image dimensions are too small. The largest dimension must be at least ${MIN_IMAGE_DIMENSION_PX}px for optimal print quality.`;

      // Revoke the object URL to prevent memory leaks
      URL.revokeObjectURL(objectUrl);
      resolve({ valid: isValid, message });
    };

    img.onerror = () => {
      // If we can't load the image, it's corrupted - reject it
      URL.revokeObjectURL(objectUrl);
      resolve({ valid: false, message: "Unable to load image. The file may be corrupted." });
    };

    img.src = objectUrl;
  });

/**
 * Comprehensive validation for design lab artwork files including dimensions.
 * Handles files from cloud storage providers (Google Drive, Dropbox) on mobile
 * by materializing the file content before validation.
 *
 * @param {File} file - The file to validate
 * @returns {Promise<{ valid: boolean, message: string, file?: File }>} - Promise resolving to validation result with optional materialized file
 */
export const validateDesignLabArtworkFile = async file => {
  // First check file type (quick check, no file read needed)
  const typeValidation = validateDesignLabArtworkFileType(file);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  // Check file size before reading the file (quick check, no file read needed)
  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  // Materialize the file to handle cloud storage providers on mobile
  // This ensures the file content is actually available before validation and upload
  const { file: materializedFile, error: materializeError } = await materializeFile(file);
  if (materializeError || !materializedFile) {
    return { valid: false, message: materializeError || "Failed to read file" };
  }

  // Then check dimensions for raster images using the materialized file
  const dimensionValidation = await validateImageDimensions(materializedFile);
  if (!dimensionValidation.valid) {
    return dimensionValidation;
  }

  // Return the materialized file so the caller can use it for upload
  return { valid: true, message: "", file: materializedFile };
};

/**
 * Get design lab specific accept string for HTML file input
 * @returns {string} - Comma-separated accept string for design lab
 */
export const getDesignLabAcceptString = () => {
  const acceptArray = [];

  for (const [mimeType, extensions] of Object.entries(DESIGN_LAB_ACCEPT_CONFIG)) {
    acceptArray.push(mimeType);
    acceptArray.push(...extensions);
  }

  return acceptArray.join(",");
};
